import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { clearUserSession, saveUserSession, publicClientApi } from "../lib/api";

function formatDuration(time) {
  if (!time) return "60 Min";
  const timeStr = String(time).trim();
  if (/[a-zA-Z]/.test(timeStr)) {
    return timeStr;
  }
  if (timeStr.includes(":")) {
    const parts = timeStr.split(":");
    const hrs = parseInt(parts[0], 10);
    const mins = parseInt(parts[1], 10);
    if (!isNaN(hrs) && !isNaN(mins)) {
      if (hrs > 0 && mins > 0) {
        return `${hrs} Hr${hrs > 1 ? "s" : ""} ${mins} Min`;
      } else if (hrs > 0) {
        return `${hrs} Hr${hrs > 1 ? "s" : ""}`;
      } else if (mins > 0) {
        return `${mins} Min`;
      }
    }
  }
  if (timeStr.includes(".")) {
    const val = parseFloat(timeStr);
    if (!isNaN(val)) {
      if (val < 10) {
        const hrs = Math.floor(val);
        const mins = Math.round((val - hrs) * 60);
        if (hrs > 0 && mins > 0) {
          return `${hrs} Hr${hrs > 1 ? "s" : ""} ${mins} Min`;
        } else if (hrs > 0) {
          return `${hrs} Hr${hrs > 1 ? "s" : ""}`;
        } else if (mins > 0) {
          return `${mins} Min`;
        }
      } else {
        return `${Math.round(val)} Min`;
      }
    }
  }
  const mins = parseInt(timeStr, 10);
  if (isNaN(mins)) return timeStr;
  if (mins < 60) {
    return `${mins} Min`;
  }
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (remainingMins === 0) {
    return `${hrs} Hr${hrs > 1 ? "s" : ""}`;
  }
  return `${hrs} Hr${hrs > 1 ? "s" : ""} ${remainingMins} Min`;
}

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const calendarWeekDays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function ChevronDown() {
  return <i className="fa-solid fa-chevron-down text-[11px] text-slate-400" aria-hidden="true" />;
}

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function getWorkingHoursForDate(dateStr, weeklySchedule) {
  if (!dateStr || !weeklySchedule || weeklySchedule.length === 0) {
    return { startHour: 9, startMinute: 0, endHour: 17, endMinute: 0, isDayOff: false };
  }
  const parts = dateStr.split("-");
  const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const dayName = DAY_NAMES[date.getDay()];
  const schedule = weeklySchedule.find(
    (s) => s.day_of_week?.toLowerCase() === dayName
  );
  if (!schedule) {
    return { startHour: 9, startMinute: 0, endHour: 17, endMinute: 0, isDayOff: false };
  }
  const startParts = (schedule.start_time || "09:00").split(":");
  const endParts = (schedule.end_time || "17:00").split(":");
  return {
    startHour: parseInt(startParts[0], 10),
    startMinute: parseInt(startParts[1], 10) || 0,
    endHour: parseInt(endParts[0], 10),
    endMinute: parseInt(endParts[1], 10) || 0,
    isDayOff: !!schedule.is_day_off,
  };
}

function formatHour12(hour) {
  const suffix = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return { display, suffix };
}

function TimePicker({ value, onChange, workingHours, isToday }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const hourListRef = useRef(null);
  const minuteListRef = useRef(null);

  const { startHour, endHour, startMinute, endMinute, isDayOff } = workingHours || {
    startHour: 9, startMinute: 0, endHour: 17, endMinute: 0, isDayOff: false,
  };

  const timeParts = (value || "09:00").split(":");
  const selectedHour = parseInt(timeParts[0], 10);
  const selectedMinute = parseInt(timeParts[1], 10) || 0;

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // Auto-adjust if selectedTime is in the past on today
  useEffect(() => {
    if (!isToday) return;
    const currentTotal = currentHour * 60 + currentMinute;
    const selectedTotal = selectedHour * 60 + selectedMinute;
    if (selectedTotal < currentTotal) {
      // Adjust to next available hour/minute
      let targetH = currentHour;
      let targetM = Math.ceil(currentMinute / 5) * 5;
      if (targetM >= 60) {
        targetM = 0;
        targetH += 1;
      }
      if (targetH < startHour) {
        targetH = startHour;
        targetM = startMinute;
      }
      onChange(`${String(targetH).padStart(2, "0")}:${String(targetM).padStart(2, "0")}`);
    }
  }, [isToday, currentHour, currentMinute, selectedHour, selectedMinute, startHour, startMinute, onChange]);

  // Build available hours
  const availableHours = useMemo(() => {
    const hours = [];
    const minH = isToday ? Math.max(startHour, currentHour) : startHour;
    for (let h = minH; h <= endHour; h++) {
      hours.push(h);
    }
    return hours;
  }, [startHour, endHour, isToday, currentHour]);

  // Build available minutes for selected hour
  const availableMinutes = useMemo(() => {
    const mins = [];
    for (let m = 0; m < 60; m += 5) {
      if (isToday && selectedHour === currentHour && m < currentMinute) continue;
      // If it's the start hour, only show minutes >= startMinute
      if (selectedHour === startHour && m < startMinute) continue;
      // If it's the end hour, only show minutes <= endMinute
      if (selectedHour === endHour && m > endMinute) continue;
      mins.push(m);
    }
    return mins;
  }, [selectedHour, startHour, endHour, startMinute, endMinute, isToday, currentHour, currentMinute]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // Scroll to selected value when opening
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      const hourEl = hourListRef.current?.querySelector('[data-selected="true"]');
      if (hourEl) hourEl.scrollIntoView({ block: "center", behavior: "smooth" });
      const minEl = minuteListRef.current?.querySelector('[data-selected="true"]');
      if (minEl) minEl.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  }, [isOpen]);

  const handleHourClick = (h) => {
    let min = selectedMinute;
    // Clamp minute if out of range for the new hour
    if (h === startHour && min < startMinute) min = startMinute;
    if (h === endHour && min > endMinute) min = endMinute;
    // Snap to nearest 5
    min = Math.round(min / 5) * 5;
    if (min >= 60) min = 55;
    onChange(`${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`);
  };

  const handleMinuteClick = (m) => {
    onChange(`${String(selectedHour).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  };

  const { display: displayHour, suffix } = formatHour12(selectedHour);
  const displayValue = `${displayHour}:${String(selectedMinute).padStart(2, "0")} ${suffix}`;

  const { display: startDisplay, suffix: startSuffix } = formatHour12(startHour);
  const { display: endDisplay, suffix: endSuffix } = formatHour12(endHour);
  const rangeLabel = `${startDisplay}:${String(startMinute).padStart(2, "0")} ${startSuffix} – ${endDisplay}:${String(endMinute).padStart(2, "0")} ${endSuffix}`;

  if (isDayOff) {
    return (
      <div className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-sm font-semibold text-slate-400">
        <i className="fa-regular fa-calendar-xmark mr-2 text-xs" aria-hidden="true" />
        Day off — not available
      </div>
    );
  }

  if (availableHours.length === 0) {
    return (
      <div className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-sm font-semibold text-slate-400">
        <i className="fa-regular fa-calendar-xmark mr-2 text-xs" aria-hidden="true" />
        Working hours ended for today
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative mt-2">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={`flex h-12 w-full items-center justify-between rounded-xl border bg-white px-4 text-left shadow-sm transition ${isOpen
          ? "border-blue-400 ring-2 ring-blue-100"
          : "border-slate-200 hover:border-blue-300"
          }`}
      >
        <span className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <i className="fa-regular fa-clock text-blue-600 text-sm" aria-hidden="true" />
          </span>
          <span className="text-sm font-bold text-slate-900">{displayValue}</span>
        </span>
        <i
          className={`fa-solid fa-chevron-down text-[10px] text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
            }`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-[52px] z-40 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.15)]" style={{ animation: "fadeSlideDown 0.15s ease-out" }}>
          {/* Range header */}
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Working hours</span>
            <span className="text-[11px] font-bold text-blue-700">{rangeLabel}</span>
          </div>

          {/* Scrollable columns */}
          <div className="flex divide-x divide-slate-100" style={{ height: "200px" }}>
            {/* Hours column */}
            <div ref={hourListRef} className="flex-1 overflow-y-auto py-1 scrollbar-thin">
              {availableHours.map((h) => {
                const { display: hDisp, suffix: hSuffix } = formatHour12(h);
                const isSelected = h === selectedHour;
                return (
                  <button
                    type="button"
                    key={h}
                    data-selected={isSelected}
                    onClick={() => handleHourClick(h)}
                    className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors ${isSelected
                      ? "bg-blue-600 font-extrabold text-white"
                      : "font-semibold text-slate-700 hover:bg-blue-50"
                      }`}
                  >
                    <span>{hDisp}</span>
                    <span className={`text-[10px] font-bold ${isSelected ? "text-blue-200" : "text-slate-400"}`}>{hSuffix}</span>
                  </button>
                );
              })}
            </div>

            {/* Minutes column */}
            <div ref={minuteListRef} className="flex-1 overflow-y-auto py-1 scrollbar-thin">
              {availableMinutes.map((m) => {
                const isSelected = m === selectedMinute;
                return (
                  <button
                    type="button"
                    key={m}
                    data-selected={isSelected}
                    onClick={() => handleMinuteClick(m)}
                    className={`flex w-full items-center justify-center px-4 py-2 text-sm transition-colors ${isSelected
                      ? "bg-blue-600 font-extrabold text-white"
                      : "font-semibold text-slate-700 hover:bg-blue-50"
                      }`}
                  >
                    :{String(m).padStart(2, "0")}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer with confirm */}
          <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-2">
            <span className="text-xs font-semibold text-slate-500">{displayValue}</span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="h-7 rounded-lg bg-blue-600 px-3 text-[11px] font-bold text-white transition hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Inline animation keyframe */}
      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .scrollbar-thin::-webkit-scrollbar { width: 4px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 999px; }
      `}</style>
    </div>
  );
}

const isSuccessMessage = (msg) => {
  if (!msg) return false;
  const lower = msg.toLowerCase();
  return lower.includes("success") ||
    lower.includes("saved") ||
    lower.includes("updated") ||
    lower.includes("created") ||
    lower.includes("deleted") ||
    lower.includes("loaded") ||
    lower.includes("sent") ||
    lower.includes("verified");
};

// SearchIcon helper purged

function getFloatingPopoverPosition(event, width = 420, estimatedHeight = 640) {
  const gutter = 16;
  const topBoundary = typeof window !== "undefined" && window.innerWidth >= 768 ? 86 : gutter;
  const fallbackLeft = typeof window === "undefined" ? gutter : Math.max(gutter, Math.round((window.innerWidth - width) / 2));
  const fallbackTop = topBoundary;
  const fallbackMaxHeight = typeof window === "undefined" ? estimatedHeight : Math.max(300, window.innerHeight - fallbackTop - gutter);
  const fallback = { left: fallbackLeft, top: fallbackTop, maxHeight: fallbackMaxHeight };

  if (!event?.currentTarget || typeof window === "undefined") return fallback;

  const rect = event.currentTarget.getBoundingClientRect();
  const panelWidth = Math.min(width, window.innerWidth - gutter * 2);
  const maxPanelHeight = Math.min(estimatedHeight, window.innerHeight - topBoundary - gutter);
  const fitsRight = rect.right + gutter + panelWidth <= window.innerWidth;
  const rawLeft = fitsRight ? rect.right + gutter : rect.left - panelWidth - gutter;
  const left = Math.min(Math.max(gutter, rawLeft), Math.max(gutter, window.innerWidth - panelWidth - gutter));
  const canOpenDown = rect.top + maxPanelHeight + gutter <= window.innerHeight;
  const canOpenUp = rect.bottom - maxPanelHeight >= topBoundary;
  const centeredTop = rect.top + rect.height / 2 - maxPanelHeight / 2;
  const rawTop = canOpenDown ? rect.top - 8 : canOpenUp ? rect.bottom - maxPanelHeight : centeredTop;
  const top = Math.min(Math.max(topBoundary, rawTop), Math.max(topBoundary, window.innerHeight - maxPanelHeight - gutter));
  const maxHeight = Math.max(300, window.innerHeight - top - gutter);

  return { left: Math.round(left), top: Math.round(top), maxHeight: Math.round(maxHeight) };
}

function SelectShell({ label, value, active, onClick, children, wide, innerRef }) {
  return (
    <div ref={innerRef} className={`relative ${wide ? "lg:min-w-[300px]" : "lg:min-w-[220px]"} flex-1`}>
      <button
        type="button"
        onClick={onClick}
        className={`h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-left text-sm font-semibold text-slate-900 shadow-sm transition hover:border-blue-300 ${active ? "border-blue-400 ring-2 ring-blue-100" : ""
          }`}
        aria-label={label}
      >
        <span className="flex items-center justify-between gap-4">
          <span className="truncate">{value}</span>
          <ChevronDown />
        </span>
      </button>
      {active && children}
    </div>
  );
}

const getFriendlyErrorMessage = (errorMsg) => {
  if (!errorMsg) return "An unexpected error occurred. Please try again.";
  const lower = errorMsg.toLowerCase();
  if (lower.includes("failed to fetch") || lower.includes("networkerror") || lower.includes("inbound response pipeline")) {
    return "We are unable to reach our services. Please check your internet connection or try again shortly.";
  }
  if (lower.includes("unexpected token") || lower.includes("json") || lower.includes("parsing")) {
    return "The server returned an unreadable response. This might be a temporary issue; please try again shortly.";
  }
  return errorMsg;
};

export default function BookingExperience({
  compact = false,
  vendorProfile,
  vendorServices = [],
  userSession: propUserSession,
  onSessionChange,
  onBookingConfirmed,
}) {
  const [userSession, setUserSession] = useState(propUserSession);

  useEffect(() => {
    setUserSession(propUserSession);
  }, [propUserSession]);

  const [service, setService] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const serviceDropdownRef = useRef(null);

  useEffect(() => {
    if (openMenu !== "service") return;

    function handleClickOutside(event) {
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenu]);

  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth()); // 0-indexed
  const [selectedDateString, setSelectedDateString] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  );
  const [artistSlots, setArtistSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customTime, setCustomTime] = useState("09:00");
  const [vendorWeeklySchedule, setVendorWeeklySchedule] = useState([]);

  const isToday = useMemo(() => {
    const t = new Date();
    const todayStr = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    return selectedDateString === todayStr;
  }, [selectedDateString]);

  const handleCustomTimeChange = (timeStr) => {
    setCustomTime(timeStr);
    setSelectedSlot({ id: 0, start_time: timeStr });
  };

  const [bookingStep, setBookingStep] = useState("slots");
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [datePopoverPosition, setDatePopoverPosition] = useState({ left: 320, top: 120, maxHeight: 640 });

  const [otpForm, setOtpForm] = useState({ email: "", otp: "" });
  const [otpMessage, setOtpMessage] = useState("");
  const [isOtpSaving, setIsOtpSaving] = useState(false);

  const [detailsForm, setDetailsForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    note: "",
  });
  const [isBookingSlot, setIsBookingSlot] = useState(false);
  const [bookingErrorMessage, setBookingErrorMessage] = useState("");

  const [appointmentDraft, setAppointmentDraft] = useState({
    title: "",
    customer: "",
    note: "",
  });

  // Default to first service on load
  useEffect(() => {
    if (vendorServices && vendorServices.length > 0) {
      setService(vendorServices[0]);
    }
  }, [vendorServices]);

  // Fetch vendor's weekly schedule for working hours
  useEffect(() => {
    if (!vendorProfile?.id) return;
    publicClientApi.getVendorWeeklySchedule(vendorProfile.id)
      .then((res) => {
        const scheduleData = res.data || res.schedule;
        if (res.success && scheduleData) {
          setVendorWeeklySchedule(scheduleData);
        }
      })
      .catch((err) => {
        console.error("[Weekly Schedule] Failed to load:", err);
      });
  }, [vendorProfile?.id]);

  // Compute working hours for currently selected date
  const currentWorkingHours = useMemo(
    () => getWorkingHoursForDate(selectedDateString, vendorWeeklySchedule),
    [selectedDateString, vendorWeeklySchedule]
  );

  // Sync profile details if customer logs in
  useEffect(() => {
    if (userSession) {
      const verifiedEmail = userSession.user?.phone || otpForm.email || "";
      setDetailsForm({
        fullName: userSession.user?.fullname || userSession.user?.name || "",
        email: userSession.user?.email || verifiedEmail || "",
        phone: verifiedEmail,
        note: "",
      });
    }
  }, [userSession, otpForm.email]);

  // Stable refs so loadSlotsForDate can read current values without being
  // listed as reactive dependencies (avoids infinite re-render loops).
  const selectedSlotRef = useRef(null);
  selectedSlotRef.current = selectedSlot;
  const bookingStepRef = useRef("slots");
  bookingStepRef.current = bookingStep;

  // Load slots for selected date

  const loadSlotsForDate = useCallback(async (dateStr) => {
    if (!vendorProfile?.id) return;
    try {
      const response = await publicClientApi.getBookingSlots(vendorProfile.id, dateStr);
      const slots = response.slots || response.data || [];
      setArtistSlots(slots);

      const currentStep = bookingStepRef.current;

      // If the user is past slot selection (on details/confirmed), never touch their chosen slot
      if (currentStep === "details" || currentStep === "confirmed") {
        return;
      }

      // Default to first slot start time or 09:00
      const openSlot = slots[0];
      const defaultTime = openSlot ? String(openSlot.start_time || openSlot.startTime || "09:00").slice(0, 5) : "09:00";
      setCustomTime(defaultTime);
      setSelectedSlot({ id: 0, start_time: defaultTime });
    } catch (err) {
      console.error("[Slots Load] Error:", err);
      setArtistSlots([]);
      if (bookingStepRef.current === "slots") {
        setSelectedSlot(null);
      }
    }
  }, [vendorProfile?.id]); // intentionally omits selectedSlot/bookingStep — use refs instead

  useEffect(() => {
    if (vendorProfile?.id && selectedDateString) {
      loadSlotsForDate(selectedDateString);
    }
  }, [vendorProfile?.id, selectedDateString, loadSlotsForDate]);

  const loadSlotsRef = useRef(null);
  const dateStrRef = useRef(null);

  useEffect(() => {
    loadSlotsRef.current = loadSlotsForDate;
    dateStrRef.current = selectedDateString;
  });

  useEffect(() => {
    if (!vendorProfile?.id) return;

    const serverBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
    const cleanServerBase = serverBase.replace(/\/api$/, "");
    const sseUrl = `${cleanServerBase}/api/realtime-sync?artistId=${vendorProfile.id}`;
    console.log("[SSE BookingExperience] Registering real-time listener:", sseUrl);

    const eventSource = new EventSource(sseUrl);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("[SSE Event BookingExperience] Received:", data);
        if (data.type === "booking_confirmed" || data.type === "slot_updated") {
          if (loadSlotsRef.current && dateStrRef.current) {
            loadSlotsRef.current(dateStrRef.current);
          }
        }
      } catch (err) {
        console.error("[SSE parse error]", err);
      }
    };

    eventSource.onerror = (err) => {
      console.error("[SSE BookingExperience Error]:", err);
    };

    return () => {
      eventSource.close();
    };
  }, [vendorProfile?.id]);

  const servicesByCategory = useMemo(() => {
    if (!vendorServices || vendorServices.length === 0) return [];
    const groups = {};
    vendorServices.forEach(item => {
      const category = item.category_name || item.category || "General";
      if (!groups[category]) {
        groups[category] = { category, items: [] };
      }
      groups[category].items.push(item);
    });
    return Object.values(groups);
  }, [vendorServices]);

  // const professionalLabel = vendorProfile?.fullname || "Artist Professional";

  const getFormattedDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const selectedFullLabel = useMemo(() => {
    return getFormattedDate(selectedDateString);
  }, [selectedDateString]);

  const selectedDateStatus = useMemo(() => {
    if (artistSlots.length === 0) return null;
    const hasOpenSlots = artistSlots.length > 0;
    return hasOpenSlots ? "available" : "booked";
  }, [artistSlots]);

  const checkIsPast = (dateStr) => {
    const t = new Date();
    const todayStr = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    return dateStr < todayStr;
  };

  const calendarCells = useMemo(() => {
    const cells = [];
    const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
    const totalDays = new Date(currentYear, currentMonth + 1, 0).getDate();
    const prevTotalDays = new Date(currentYear, currentMonth, 0).getDate();

    // Previous month padding
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const day = prevTotalDays - i;
      const m = currentMonth === 0 ? 11 : currentMonth - 1;
      const y = currentMonth === 0 ? currentYear - 1 : currentYear;
      const dateString = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push({ day, month: m, year: y, isCurrentMonth: false, dateString });
    }
    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const dateString = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push({ day, month: currentMonth, year: currentYear, isCurrentMonth: true, dateString });
    }
    // Next month padding
    const remaining = 42 - cells.length;
    for (let day = 1; day <= remaining; day++) {
      const m = currentMonth === 11 ? 0 : currentMonth + 1;
      const y = currentMonth === 11 ? currentYear + 1 : currentYear;
      const dateString = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      cells.push({ day, month: m, year: y, isCurrentMonth: false, dateString });
    }
    return cells;
  }, [currentYear, currentMonth]);

  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      if (prev === 0) {
        setCurrentYear(y => y - 1);
        return 11;
      }
      return prev - 1;
    });
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      if (prev === 11) {
        setCurrentYear(y => y + 1);
        return 0;
      }
      return prev + 1;
    });
  };

  const chooseCalendarDate = async (cell, openDialog = false, event) => {
    if (!cell.isCurrentMonth) return;
    setSelectedDateString(cell.dateString);
    if (openDialog) {
      setDatePopoverPosition(getFloatingPopoverPosition(event));
      setDateModalOpen(true);
    }
  };

  const openSelectedDatePopover = (event) => {
    setDatePopoverPosition(getFloatingPopoverPosition(event));
    setDateModalOpen(true);
  };

  const handleSendOtp = async () => {
    if (!otpForm.email.trim()) {
      setOtpMessage("Enter an email address first.");
      return;
    }
    setIsOtpSaving(true);
    setOtpMessage("");
    try {
      const response = await publicClientApi.getClientOtp(otpForm.email.trim());
      setOtpMessage(response.otp ? `OTP sent. Local dev OTP: ${response.otp}` : response.message || "OTP sent.");
    } catch (error) {
      setOtpMessage(error.message || "Unable to send OTP.");
    } finally {
      setIsOtpSaving(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpForm.email.trim() || !otpForm.otp.trim()) {
      setOtpMessage("Enter email and OTP.");
      return;
    }
    setIsOtpSaving(true);
    setOtpMessage("");
    try {
      const response = await publicClientApi.verifyClientOtp(otpForm.email.trim(), otpForm.otp.trim());
      saveUserSession(response.data || response);
      setUserSession(response.data || response);
      if (onSessionChange) onSessionChange(response.data || response);
      setOtpMessage(response.message || "OTP verified.");
    } catch (error) {
      setOtpMessage(error.message || "Unable to verify OTP.");
    } finally {
      setIsOtpSaving(false);
    }
  };

  const handleSignOut = () => {
    clearUserSession();
    setUserSession(null);
    if (onSessionChange) onSessionChange(null);
    setOtpForm({ email: "", otp: "" });
    setOtpMessage("");
  };

  const handleConfirmBooking = async () => {
    if (!service) {
      setBookingErrorMessage("Please select a service first.");
      return;
    }
    if (!selectedSlot) {
      setBookingErrorMessage("Please select an available time slot first.");
      return;
    }
    const resolvedSlotId = Number(selectedSlot?.id);
    if (Number.isNaN(resolvedSlotId)) {
      setBookingErrorMessage("Your selected time slot is invalid. Please go back and pick a slot.");
      return;
    }
    if (!userSession?.token) {
      setBookingErrorMessage("Please verify your phone number to sign in.");
      return;
    }
    if (!detailsForm.fullName.trim() || !detailsForm.email.trim()) {
      setBookingErrorMessage("Please enter your name and email.");
      return;
    }

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (selectedDateString < todayStr) {
      setBookingErrorMessage("Please select today's date or a future date.");
      return;
    }

    const workingHours = getWorkingHoursForDate(selectedDateString, vendorWeeklySchedule);
    if (workingHours.isDayOff) {
      setBookingErrorMessage("Cannot book on a day off.");
      return;
    }

    const { startHour, startMinute, endHour, endMinute } = workingHours;
    const timeParts = customTime.split(":");
    const selectedH = parseInt(timeParts[0], 10);
    const selectedM = parseInt(timeParts[1], 10) || 0;
    const selectedTotal = selectedH * 60 + selectedM;

    if (selectedTotal < startHour * 60 + startMinute || selectedTotal > endHour * 60 + endMinute) {
      setBookingErrorMessage("Selected time is outside working hours.");
      return;
    }

    if (selectedDateString === todayStr) {
      const currentTotal = now.getHours() * 60 + now.getMinutes();
      if (selectedTotal < currentTotal) {
        setBookingErrorMessage("Cannot book a time in the past.");
        return;
      }
    }

    setIsBookingSlot(true);
    setBookingErrorMessage("");
    try {
      const payload = {
        artistId: Number(vendorProfile?.id),
        slotId: resolvedSlotId,
        bookingDate: selectedDateString,
        serviceId: Number(service?.id || service?.serviceid),
        fullName: detailsForm.fullName.trim(),
        email: detailsForm.email.trim(),
        note: detailsForm.note.trim(),
        title: appointmentDraft.title?.trim() || service?.name || "Booking",
        bookingTime: customTime,
      };

      const response = await publicClientApi.confirmBooking(payload);
      if (response.success) {
        setBookingStep("confirmed");
        if (onBookingConfirmed) {
          onBookingConfirmed();
        }
      } else {
        throw new Error(response.message || "Failed to confirm booking.");
      }
    } catch (error) {
      console.error("[Booking Conf] Error:", error);
      if (error.message && error.message.toLowerCase().includes("unauthorized")) {
        handleSignOut();
        setBookingErrorMessage("Your session has expired or is invalid. Please sign in again.");
      } else {
        setBookingErrorMessage(getFriendlyErrorMessage(error.message || "Failed to confirm booking. Please try again."));
      }
    } finally {
      setIsBookingSlot(false);
    }
  };

  return (
    <section className={compact ? "bg-transparent" : "bg-[#F5F9FF]"}>
      <div className={compact ? "" : "max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        <div className="overflow-visible rounded-[28px] border border-blue-100 bg-white shadow-[0_30px_90px_rgba(14,63,182,0.12)]">

          {/* Header selectors */}
          <div className="border-b border-slate-100 bg-gradient-to-r from-white via-blue-50/60 to-white p-4 sm:p-6">
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Select service</p>
              <SelectShell
                innerRef={serviceDropdownRef}
                label="Select service"
                value={service ? `${service.name} (${formatDuration(service.time)})` : "Choose a Service"}
                active={openMenu === "service"}
                onClick={() => setOpenMenu(openMenu === "service" ? null : "service")}
                wide
              >
                <div className="absolute left-0 right-0 top-[50px] z-30 max-h-[300px] overflow-y-auto rounded-b-lg border border-slate-200 bg-white py-3 shadow-xl">
                  {servicesByCategory.map((group) => (
                    <div key={group.category} className="pb-2">
                      <div className="px-4 pb-2 text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
                        {group.category}
                      </div>
                      {group.items.map((item) => (
                        <button
                          type="button"
                          key={item.id || item.serviceid}
                          onClick={() => {
                            setService(item);
                            setOpenMenu(null);
                          }}
                          className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left text-sm text-slate-800 transition hover:bg-blue-50"
                        >
                          <span className="font-semibold">{item.name} ({formatDuration(item.time)})</span>
                          <span className="shrink-0 text-xs text-slate-500">${Number(item.price).toFixed(2)}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                  {servicesByCategory.length === 0 && (
                    <p className="text-center text-sm font-semibold text-slate-500 py-4">No active services found.</p>
                  )}
                </div>
              </SelectShell>

              {/* <button
                type="button"
                onClick={() => {
                  setBookingStep("slots");
                }}
                className="h-12 rounded-xl bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] px-8 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:shadow-blue-300 xl:w-[120px]"
              >
                Search
              </button> */}
            </div>
          </div>

          {/* Grid Layout (Month Calendar + Sidebar Details) */}
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Choose a date</p>
                <h2 className="mt-2 text-3xl font-extrabold text-[#191E49]">{monthNames[currentMonth]} {currentYear}</h2>
                <p className="mt-2 text-sm font-semibold text-slate-500">Select any date in the month to check appointments.</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-600 shadow-sm">
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                  Available
                </div>
                <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-600 shadow-sm">
                  <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
                  Not Available
                </div>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_430px]">

              {/* Calendar Grid */}
              <div className="overflow-hidden rounded-[22px] border border-slate-300 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
                <div className="flex items-center justify-between gap-4 border-b border-slate-300 bg-slate-50 p-4">
                  <button
                    type="button"
                    onClick={handlePrevMonth}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-slate-300 bg-white text-slate-500 transition hover:border-blue-300 hover:text-blue-700"
                    aria-label="Previous month"
                  >
                    <i className="fa-solid fa-chevron-left text-xs" aria-hidden="true" />
                  </button>
                  <div className="text-center">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Full month calendar</p>
                    <h3 className="mt-1 text-2xl font-extrabold text-[#191E49]">{monthNames[currentMonth]} {currentYear}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={handleNextMonth}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-slate-300 bg-white text-slate-500 transition hover:border-blue-300 hover:text-blue-700"
                    aria-label="Next month"
                  >
                    <i className="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-3 border-b border-slate-300 bg-slate-50 p-3 text-center text-[11px] font-extrabold tracking-[0.12em] text-slate-600">
                  {calendarWeekDays.map((day) => (
                    <span key={day} className="rounded-xl border border-slate-200 bg-white/80 py-2.5 shadow-sm">{day}</span>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-3 bg-slate-100/70 p-3">
                  {calendarCells.map((cell, index) => {
                    const { day, isCurrentMonth, dateString } = cell;
                    const isPast = checkIsPast(dateString);
                    const cellWorkingHours = getWorkingHoursForDate(dateString, vendorWeeklySchedule);
                    const isCellDayOff = cellWorkingHours.isDayOff;
                    const active = selectedDateString === dateString && isCurrentMonth;

                    let statusLabel = isPast ? "Past" : isCurrentMonth ? "Open" : "";
                    if (isCurrentMonth && !isPast && isCellDayOff) {
                      statusLabel = "Closed";
                    }

                    return (
                      <button
                        type="button"
                        key={`${index}-${day}`}
                        onClick={(event) => isCurrentMonth && !isPast && !isCellDayOff && chooseCalendarDate(cell, true, event)}
                        disabled={!isCurrentMonth || isPast || isCellDayOff}
                        className={`relative flex min-h-[86px] flex-col items-start justify-between rounded-2xl border p-2 text-left shadow-sm transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:min-h-[110px] sm:p-3 ${active
                          ? "border-blue-400 bg-white text-slate-900 ring-2 ring-blue-500 shadow-[0_18px_35px_rgba(37,99,235,0.16)]"
                          : (!isCurrentMonth || isPast || isCellDayOff)
                            ? "cursor-default border-dashed border-slate-200 bg-white/45 text-slate-300 opacity-70 shadow-none"
                            : "border-slate-200 bg-white text-slate-800 hover:-translate-y-1 hover:border-blue-300 hover:bg-white hover:shadow-[0_18px_35px_rgba(15,23,42,0.12)]"
                          }`}
                      >
                        <span className={`grid h-7 w-7 place-items-center rounded-full text-sm font-extrabold sm:h-8 sm:w-8 sm:text-base ${active ? "bg-blue-600 text-white" : ""
                          }`}>
                          {day}
                        </span>
                        {isCurrentMonth && (
                          <span className={`mt-3 flex max-w-full items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-bold ${(!isPast && !isCellDayOff)
                            ? "border-blue-100 bg-blue-50 text-slate-700"
                            : "border-slate-100 bg-slate-50 text-slate-400"
                            }`}>
                            <span className={`h-2 w-2 rounded-full ${(isPast || isCellDayOff) ? "bg-slate-200" : "bg-blue-600"
                              }`} />
                            <span className="truncate">
                              {statusLabel}
                            </span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* <div className="flex flex-col gap-3 border-t border-slate-300 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Selected date</p>
                    <p className="mt-1 text-sm font-extrabold text-slate-900">
                      {selectedFullLabel}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openSelectedDatePopover}
                    className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
                  >
                    View appointments
                  </button>
                </div> */}
              </div>

              {/* Sidebar Booking Summary & Confirmation Form */}
              <div className="self-start rounded-3xl border border-blue-100 bg-white p-5 shadow-[0_18px_50px_rgba(14,63,182,0.09)] lg:p-6 xl:sticky xl:top-24">

                {/* Details Banner */}
                <div className="rounded-2xl bg-blue-50/80 p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Selected service</p>
                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    <p className="flex justify-between gap-4">
                      <span className="font-semibold">
                        {service ? `${service.name} (${formatDuration(service.time)})` : "No Service Selected"}
                      </span>
                      <span className="font-bold text-slate-900">{service?.price ? `$${Number(service.price).toFixed(2)}` : ""}</span>
                    </p>
                    <p className="flex justify-between gap-4">
                      <span className="font-semibold">
                        {selectedFullLabel}
                        {selectedSlot && ` · ${String(selectedSlot.start_time || selectedSlot.startTime).slice(0, 5)}`}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Form Panels based on active step */}
                <div className="mt-8">
                  {bookingStep === "slots" && (
                    <div>
                      <div className="text-center">
                        <i className="fa-regular fa-calendar-check text-2xl text-blue-700" aria-hidden="true" />
                        <h3 className="mt-6 text-2xl font-extrabold text-[#06142F]">Available appointments</h3>
                        <p className="mt-3 text-sm font-semibold text-slate-500">{selectedFullLabel}</p>
                      </div>

                      <div className="mt-7 space-y-2">
                        <label className="block text-left">
                          <span className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Select Booking Time</span>
                          <TimePicker
                            value={customTime}
                            onChange={handleCustomTimeChange}
                            workingHours={currentWorkingHours}
                            isToday={isToday}
                          />
                        </label>
                      </div>

                      <div className="mt-7 rounded-xl border border-blue-100 bg-blue-50/70 p-4">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Booking summary</p>
                        <div className="mt-3 space-y-2 text-sm text-slate-700">
                          <p className="flex justify-between gap-4">
                            <span>
                              {service?.name || "Service"}
                              {service && (
                                <span className="ml-2 text-xs font-semibold text-slate-500">
                                  ({formatDuration(service.time)})
                                </span>
                              )}
                            </span>
                            <span className="font-bold text-slate-900">{service?.price ? `$${Number(service.price).toFixed(2)}` : ""}</span>
                          </p>
                          <p className="flex justify-between gap-4">
                            <span>Start Time</span>
                            <span className="font-semibold text-slate-950">
                              {selectedSlot ? `${String(selectedSlot.start_time || selectedSlot.startTime).slice(0, 5)}` : "None Selected"}
                            </span>
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setBookingStep("details");
                        }}
                        disabled={!selectedSlot}
                        className="mt-5 h-11 w-full rounded-md bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Continue
                      </button>
                    </div>
                  )}

                  {bookingStep === "details" && (
                    <div>
                      <div className="text-center">
                        <i className="fa-regular fa-id-card text-2xl text-blue-700" aria-hidden="true" />
                        <h3 className="mt-6 text-2xl font-extrabold text-[#06142F]">Your details</h3>
                        <p className="mt-3 text-sm font-semibold text-slate-500">Sign in with phone to confirm booking.</p>
                      </div>

                      {!userSession?.token ? (
                        <div className="mt-7 space-y-4">
                          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Authentication Required</p>
                          <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
                            <input
                              type="email"
                              value={otpForm.email}
                              onChange={(e) => setOtpForm(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="Email address"
                              className="h-11 rounded-xl border border-slate-200 px-4 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            />
                            <button
                              type="button"
                              onClick={handleSendOtp}
                              disabled={isOtpSaving}
                              className="h-11 rounded-xl bg-blue-600 px-4 text-sm font-bold text-white transition hover:bg-blue-700 disabled:opacity-70"
                            >
                              Send OTP
                            </button>
                            <input
                              value={otpForm.otp}
                              onChange={(e) => setOtpForm(prev => ({ ...prev, otp: e.target.value }))}
                              placeholder="Verification OTP"
                              className="h-11 rounded-xl border border-slate-200 px-4 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                            />
                            <button
                              type="button"
                              onClick={handleVerifyOtp}
                              disabled={isOtpSaving}
                              className="h-11 rounded-xl border border-blue-200 px-4 text-sm font-bold text-blue-700 transition hover:bg-blue-50 disabled:opacity-70"
                            >
                              Verify
                            </button>
                          </div>
                          {otpMessage && (
                            <div className={`rounded-xl border px-4 py-3 text-sm font-bold ${isSuccessMessage(otpMessage) ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-red-100 bg-red-50 text-red-700"}`}>
                              {otpMessage}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="mt-4 p-3 border border-emerald-100 bg-emerald-50 rounded-xl flex items-center justify-between">
                            <span className="text-xs font-bold text-emerald-800">Signed in successfully</span>
                            <button
                              type="button"
                              onClick={handleSignOut}
                              className="text-xs font-bold text-red-600 hover:underline"
                            >
                              Sign out
                            </button>
                          </div>
                          <div className="mt-7 space-y-4">
                            <label className="block">
                              <span className="text-sm font-bold text-slate-700">Full name</span>
                              <input
                                value={detailsForm.fullName}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setDetailsForm(prev => ({ ...prev, fullName: val }));
                                  setAppointmentDraft(prev => ({ ...prev, customer: val }));
                                }}
                                className="mt-2 h-11 w-full rounded-md border border-slate-200 px-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                                placeholder="Your Name"
                              />
                            </label>
                            <label className="block">
                              <span className="text-sm font-bold text-slate-700">Any note</span>
                              <textarea
                                value={detailsForm.note}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setDetailsForm(prev => ({ ...prev, note: val }));
                                  setAppointmentDraft(prev => ({ ...prev, note: val }));
                                }}
                                className="mt-2 min-h-[88px] w-full rounded-md border border-slate-200 px-3 py-3 text-sm font-semibold outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                                placeholder="Share preferences, allergies, or event details."
                              />
                            </label>
                          </div>
                        </>
                      )}

                      {bookingErrorMessage && (
                        <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                          {bookingErrorMessage}
                        </div>
                      )}

                      <div className="mt-5 grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setBookingStep("slots")}
                          className="h-11 rounded-md border border-slate-300 bg-white text-sm font-bold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={handleConfirmBooking}
                          disabled={!userSession?.token || isBookingSlot}
                          className="h-11 rounded-md bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isBookingSlot ? "Confirming..." : "Confirm booking"}
                        </button>
                      </div>
                    </div>
                  )}

                  {bookingStep === "confirmed" && (
                    <div className="text-center">
                      <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-50 text-emerald-700">
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      </span>
                      <h3 className="mt-6 text-2xl font-extrabold text-[#06142F]">Booking confirmed</h3>
                      <p className="mt-3 text-sm font-semibold leading-6 text-slate-500">
                        Appointment confirmed for {selectedSlot ? `${String(selectedSlot.start_time || selectedSlot.startTime).slice(0, 5)}` : ""} on {selectedFullLabel}.
                      </p>
                      <div className="mt-7 rounded-xl border border-blue-100 bg-blue-50/70 p-4 text-left">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Booking reference</p>
                        <p className="mt-2 text-sm font-semibold text-slate-800">{service?.name} ({formatDuration(service?.time)})</p>
                      </div>
                      <div className="mt-5 grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setBookingStep("slots")}
                          className="h-11 rounded-md border border-blue-200 bg-white text-sm font-bold text-blue-700 transition hover:bg-blue-50"
                        >
                          Book another
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setBookingStep("slots");
                            setSelectedSlot(null);
                          }}
                          className="h-11 rounded-md border border-slate-300 bg-white text-sm font-bold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Popover dialog */}
      {dateModalOpen && createPortal(
        <div
          className="fixed z-50 flex w-[min(420px,calc(100vw-32px))] flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.24)]"
          role="dialog"
          aria-modal="false"
          style={datePopoverPosition}
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Appointment</p>
              <h4 className="mt-1 truncate text-xl font-extrabold text-[#191E49]">{selectedFullLabel}</h4>
              <p className="mt-1 text-xs font-semibold text-slate-500">
                {selectedDateStatus === "available" ? "Open appointments" : "Booked/Closed"}
              </p>
            </div>
            <button type="button" onClick={() => setDateModalOpen(false)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900" aria-label="Close appointment dialog">
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="grid gap-2 text-sm sm:grid-cols-2">
                <p className="min-w-0 truncate font-extrabold text-slate-900">{service?.name || "Service"} ({formatDuration(service?.time)})</p>
                <p className="font-bold text-blue-700">{service?.price ? `$${Number(service.price).toFixed(2)}` : ""}</p>
                <p className="font-semibold text-slate-500 sm:text-right">
                  {selectedSlot ? `${String(selectedSlot.start_time || selectedSlot.startTime).slice(0, 5)}` : "No Slot"}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-left">
                <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Select Booking Time</span>
                <TimePicker
                  value={customTime}
                  onChange={handleCustomTimeChange}
                  workingHours={currentWorkingHours}
                  isToday={isToday}
                />
              </label>
            </div>

          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
            <button type="button" onClick={() => setDateModalOpen(false)} className="h-10 rounded-xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setDateModalOpen(false);
                setBookingStep("details");
              }}
              disabled={!selectedSlot}
              className="h-10 rounded-xl bg-blue-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700 disabled:opacity-50"
            >
              Continue Booking
            </button>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
